import { computed, effect, inject, Injectable, signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { EVENT } from '@app/analytics/analytics.cnst'
import { AnalyticsService } from '@app/analytics/analytics.service'
import { TemperatureDataState } from '@app/cnst/add-data.cnst.js'
import { B1_ERROR, B1_TEMP_EXTRACTION_DATAFLAGS } from '@app/cnst/b1.cnst.js'
import { ROUTES } from '@app/cnst/nav.cnst.js'
import { SNACKBAR } from '@app/cnst/snackbars.cnst.js'
import { decorate, LoaderType } from '@app/decorators/decorators.js'
import {
  B1BatteryStatus,
  B1Command,
  DeviceStatus,
  ErrorStatus,
  SleepLogData,
} from '@app/model/b1.model'
import { BandRawDataState } from '@app/reducers/bandRawData.reducer'
import { BleDevice } from '@capacitor-community/bluetooth-le'
import {
  _anyToErrorObject,
  _assert,
  _Memo,
  _stringify,
  HttpRequestError,
  IANATimezone,
  localDate,
  localTime,
  NumberOfMilliseconds,
} from '@naturalcycles/js-lib'
import {
  B1SaveBatchInput,
  BatteryStatus,
  CentiBPM,
  DeciVolt,
  ErrorCode,
} from '@naturalcycles/shared'
import { concatMap, map } from 'rxjs/operators'
import { api } from './api.service.js'
import { B1PluginService } from './b1.plugin.service.js'
import { ErrorService } from './error.service.js'
import { HardwareDeviceService } from './hardwareDevice.service.js'
import { LogService } from './log.service.js'
import { appInitDone } from './milestones.js'
import { NavService } from './nav.service.js'
import { SnackbarService } from './snackbar.service.js'
import { dispatch, getState, select } from './store.service.js'

/* eslint-disable no-bitwise */

/**
 * States with predefined timeout values. All other states should be reset explicitly
 * Remember to reset timeout when current state changes
 */
const NEXT_SYNC_STATE: Partial<
  Record<
    TemperatureDataState,
    { nextState: TemperatureDataState | undefined; timeout: NumberOfMilliseconds }
  >
> = {
  [TemperatureDataState.NOT_SYNCED]: {
    nextState: TemperatureDataState.NOT_SYNCED_TIMEOUT,
    timeout: 45000,
  },
  [TemperatureDataState.NOT_SYNCED_TIMEOUT]: {
    nextState: TemperatureDataState.CANT_CONNECT_HELP,
    timeout: 30000,
  },
  [TemperatureDataState.SYNCED]: {
    nextState: undefined,
    timeout: 5000,
  },
}

@Injectable({ providedIn: 'root' })
export class B1Service {
  private analyticsService = inject(AnalyticsService)
  private b1PluginService = inject(B1PluginService)
  private errorService = inject(ErrorService)
  private hardwareDeviceService = inject(HardwareDeviceService)
  private logService = inject(LogService)
  private navService = inject(NavService)
  private snackbarService = inject(SnackbarService)

  private bandRawData$ = select(s => s.bandRawData)

  public bandDataToSave$ = this.bandRawData$.pipe(
    map(bandRawData => {
      if (!bandRawData) return undefined

      const { id: accountId } = getState().account
      return bandRawData[accountId]
    }),
  )

  public lastB1SyncDate = toSignal(select(s => s.userSettings.lastB1SyncDate))
  public hasB1SyncedToday = computed(() => {
    const date = this.lastB1SyncDate()
    return date ? localDate(date).isToday() : false
  })

  /**
   * Current syncing state of the B1.
   * Updated here in B1Service and BluetoothConnectService (to avoid circular dependency)
   */
  public readonly ongoingSyncingState = signal<TemperatureDataState | undefined>(undefined)

  public readonly todayDataSyncingState = computed(() => {
    const syncingState = this.ongoingSyncingState()
    if (syncingState === TemperatureDataState.SYNCED) return TemperatureDataState.SYNCED

    const { entryMap, todayDate } = getState().userFertility
    const todayEntry = entryMap[todayDate]

    // We don't want to show card if user has already synced temperature for today
    if (!todayEntry) return syncingState
    if (todayEntry.temperature) return undefined
    if (todayEntry.dataFlags?.some(flag => B1_TEMP_EXTRACTION_DATAFLAGS.has(flag))) return undefined

    return syncingState
  })

  private stateTimeout: ReturnType<typeof setTimeout> | undefined
  private scanningTimeout: ReturnType<typeof setTimeout> | undefined
  // TODO: set in remote config
  public scanningIntervalDuration: NumberOfMilliseconds = 500

  constructor() {
    // Subscribe to the syncingState to hide the card after a timeout unless it has changed
    effect(async () => {
      const b1SyncState = this.ongoingSyncingState()

      // Reset the timeout as soon as new state is triggered
      if (this.stateTimeout) clearTimeout(this.stateTimeout)
      if (b1SyncState === undefined) return

      // If there is no timeout predefined, just keep the state until updated
      if (!NEXT_SYNC_STATE[b1SyncState]) return

      const { nextState, timeout } = NEXT_SYNC_STATE[b1SyncState]
      this.stateTimeout = setTimeout(() => {
        this.ongoingSyncingState.set(nextState)
      }, timeout)
    })
  }

  @_Memo()
  public init(): void {
    this.bandDataToSave$
      .pipe(
        concatMap(async sleepLogsData => {
          if (!sleepLogsData?.length) return
          await this.saveBandData(sleepLogsData)
        }),
      )
      .subscribe()
  }

  public async verifyConnectedDevice(device: BleDevice): Promise<BleDevice | undefined> {
    const connected = await this.checkConnected()
    if (!connected) return undefined

    const serialNumber = await this.b1PluginService.getSerialNumber()
    if (serialNumber === device.name) return device

    this.logService.logBleAction(
      `Connected B1 name (${serialNumber}) doesn't match expected: ${device.name}`,
    )

    // TODO: should we disconnect from the wrong device?
    return undefined
  }

  public async startScanning(callback: (result: BleDevice) => Promise<boolean>): Promise<void> {
    if (getState().hwDevice) this.ongoingSyncingState.set(TemperatureDataState.NOT_SYNCED)

    await this.b1PluginService.initialize()

    // Make sure we are not connected to a different device
    const connected = await this.verifyNotConnected()
    if (connected) await this.b1PluginService.disconnect()
    await this.b1PluginService.scan()

    const foundDevices = new Set<string>()

    this.scanningTimeout = this.scanForDevice(foundDevices, callback)
  }

  public scanForDevice(
    foundDevices: Set<string>,
    callback: (result: BleDevice) => Promise<boolean>,
  ): ReturnType<typeof setTimeout> {
    return setTimeout(async () => {
      const devices = await this.b1PluginService.getDevices()
      const foundDevice = devices.some(async name => {
        if (foundDevices.has(name)) return

        // Check if band has temporary name (when band hasn't been initialized yet)
        if (/B1_X+/.test(name)) {
          this.logService.logBleAction(`Found B1 name (${name}) that wasn't expected.`)
          this.analyticsService.trackEvent(EVENT.BAND_UNDEFINED_MAC_ADDRESS, { name })
          return
        }

        foundDevices.add(name)
        // FIXME: what to do with deviceId?
        const deviceId = await this.nameToUUID(name)
        return await callback({ deviceId, name })
      })
      // TODO: remove debug
      console.warn('[dev]', '[b1] devices', devices)

      if (foundDevice) return
      // Continue looking for a device
      clearTimeout(this.scanningTimeout)
      this.scanningTimeout = this.scanForDevice(foundDevices, callback)
    }, this.scanningIntervalDuration)
  }

  public async stopScanning(): Promise<void> {
    await this.b1PluginService.stopScan()

    if (!this.scanningTimeout) return
    clearTimeout(this.scanningTimeout)
    this.scanningTimeout = undefined
  }

  /**
   * @throws Error if we are connected to a different band
   */
  public async connect(deviceName: string): Promise<void> {
    const connected = await this.verifyNotConnected()
    if (!connected) return await this.b1PluginService.connect(deviceName)

    const serialNumber = await this.b1PluginService.getSerialNumber()
    _assert(
      serialNumber === deviceName,
      `Connected to band: ${serialNumber} while trying to connect to: ${deviceName}.`,
      {
        code: B1_ERROR.WRONG_DEVICE_CONNECTED,
      },
    )
  }

  /**
   * Reads serial number, firmware version and device status and syncs current timestamp
   * @returns true if there is data to read, false otherwise
   */
  public async connectedCallback(): Promise<boolean> {
    const connected = await this.checkConnected()
    if (!connected) return false

    this.ongoingSyncingState.set(TemperatureDataState.SYNCING)

    const fwVersion = await this.readFirmwareVersion()

    // It will match device name
    const serialNumber = await this.readSerialNumber()

    const deviceStatus = await this.readDeviceStatus(serialNumber)
    await this.readErrorStatus(serialNumber)
    await this.readDiagnostics(serialNumber)

    if (deviceStatus?.batteryStatus !== undefined) {
      const freshBatteryStatus = this.getDeviceBatteryStatus(deviceStatus.batteryStatus)
      await this.hardwareDeviceService.saveBatteryStatus(
        freshBatteryStatus,
        deviceStatus?.batteryPercent,
      )
    }

    // In case there was not battery status value retrieved, use last known value
    const { batteryStatus } = getState().hwDevice || {}

    // Present a snackbar about low battery once connected (except My Device page + Device Details page which already mentions it in the UI
    if (
      ![ROUTES.MyDevicePage, ROUTES.DeviceDetailsPage].includes(
        this.navService.getCurrentRoute(),
      ) &&
      batteryStatus &&
      [BatteryStatus.CRITICALLY_LOW, BatteryStatus.LOW].includes(batteryStatus)
    ) {
      this.snackbarService.showSnackbar(SNACKBAR.B1_LOW_BATTERY)
    }

    await this.hardwareDeviceService.extendHardwareDevice({
      fwVersion,
      serialNumber,
    })

    await this.syncTimestamp()

    dispatch('extendUserSettings', { lastB1SyncDate: localDate.today().toISODate() })

    return !!deviceStatus?.logsToRead
  }

  /**
   * To be called from BluetoothService only!
   * Use bluetoothService.disconnect(deviceId) instead
   */
  public async disconnect(): Promise<void> {
    const connected = await this.checkConnected()
    if (!connected) return

    await this.b1PluginService.disconnect()
  }

  public async syncTimestamp(): Promise<void> {
    const timestamp = localTime.nowUnix()

    const commandData: number[] = [
      timestamp & 255,
      (timestamp >> 8) & 255,
      (timestamp >> 16) & 255,
      (timestamp >> 24) & 255,
    ]

    await this.b1PluginService.sendCommand(B1Command.SET_TIME, commandData)
    this.logService.logBleAction(`Synced timestamp: ${timestamp}`)
  }

  public async readMeasurementData(readAll = false): Promise<void> {
    const connected = await this.checkConnected()
    if (!connected) return this.ongoingSyncingState.set(undefined)

    const name = await this.readSerialNumber()

    this.analyticsService.trackEvent(EVENT.BAND_READ_DATA_STARTED, { name })

    const sleepData = readAll
      ? await this.b1PluginService.getAllLogs()
      : await this.b1PluginService.getNewLogs()
    const indexes = sleepData?.map(s => s.timestamp) ?? []
    this.logService.logBleAction(`Read measurements with IDs: ${JSON.stringify(indexes)}`)
    console.warn('[dev]', '[b1] sleepData', sleepData)

    this.analyticsService.trackEvent(EVENT.BAND_READ_DATA, {
      indexes: JSON.stringify(indexes),
      dataLength: sleepData?.length || 0,
      name,
    })
    if (!sleepData) return this.ongoingSyncingState.set(TemperatureDataState.INCOMPLETE_DATA)

    const { id } = getState().account

    dispatch('extendBandRawData', { [id]: sleepData } satisfies BandRawDataState)
    this.analyticsService.trackEvent(EVENT.BAND_STASHED_DATA, { name })

    const acknowledged = await this.b1PluginService.ackLogs()

    this.analyticsService.trackEvent(
      acknowledged ? EVENT.BAND_ACKNOWLEDGED_DATA : EVENT.BAND_ACKNOWLEDGE_DATA_FAILED,
      { name },
    )

    // Just a safety net to ensure we don't leave the syncing state
    // By this moment we already should have CALCULATING state
    if (this.ongoingSyncingState() === TemperatureDataState.SYNCING) {
      this.ongoingSyncingState.set(undefined)
    }
  }

  public reportEmptyData(): void {
    const { name } = getState().hwDevice || {}
    this.analyticsService.trackEvent(EVENT.BAND_NO_NEW_DATA, { name })
    this.ongoingSyncingState.set(TemperatureDataState.INCOMPLETE_DATA)
  }

  public async readFirmwareVersion(): Promise<string | undefined> {
    const connected = await this.checkConnected()
    if (!connected) return

    const fwVersion = await this.b1PluginService.getSoftwareRevision()
    this.logService.logBleAction(`Read firmware version: ${fwVersion}`)

    return fwVersion
  }

  public async readSerialNumber(): Promise<string | undefined> {
    const connected = await this.checkConnected()
    if (!connected) return

    const serialNumber = await this.b1PluginService.getSerialNumber()
    this.logService.logBleAction(`Read serial number: ${serialNumber}`)

    return serialNumber
  }

  public async readDeviceStatus(name?: string): Promise<DeviceStatus | undefined> {
    const connected = await this.b1PluginService.isConnected()
    if (!connected) return

    const deviceStatus = await this.b1PluginService.getDeviceStatus(name)
    this.logService.logBleAction(`Read device status: ${JSON.stringify(deviceStatus)}`)

    return deviceStatus
  }

  public async readErrorStatus(name?: string): Promise<ErrorStatus | undefined> {
    const connected = await this.b1PluginService.isConnected()
    if (!connected) return

    const errorStatus = await this.b1PluginService.getErrorStatus(name)
    this.logService.logBleAction(`Read error status: ${JSON.stringify(errorStatus)}`)

    return errorStatus
  }

  public async readDiagnostics(name?: string): Promise<unknown | undefined> {
    const connected = await this.b1PluginService.isConnected()
    if (!connected) return

    const diagnostics = await this.b1PluginService.getDiagnostics(name)
    this.logService.logBleAction(`Read diagnostics: ${JSON.stringify(diagnostics)}`)

    return diagnostics
  }

  /**
   * Converts a string into a UUID.
   * Used for mocking UUIDs since SDW plugin operates with device name but HardwareDevice.mac is required
   */
  public async nameToUUID(name: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(name)
    const hashBuffer = await crypto.subtle.digest('SHA-1', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

    return `${hashHex.slice(0, 8)}-${hashHex.slice(8, 12)}-${hashHex.slice(12, 16)}-${hashHex.slice(16, 20)}-${hashHex.slice(20, 32)}`.toUpperCase()
  }

  public getDeviceBatteryStatus(batteryStatus: B1BatteryStatus): BatteryStatus {
    if (batteryStatus === B1BatteryStatus.Limit) {
      return BatteryStatus.CRITICALLY_LOW
    }
    if (batteryStatus === B1BatteryStatus.Warning) {
      return BatteryStatus.LOW
    }
    return BatteryStatus.HIGH
  }

  public async saveBandData(sleepLogsData: SleepLogData[]): Promise<void> {
    // To avoid the race between app init and b1/saveBatch
    await appInitDone
    const payload = this.mapBandDataToB1AppOutputSpan(sleepLogsData)

    await this.sendBandData(payload)
  }

  public mapBandDataToB1AppOutputSpan(sleepLogsData: SleepLogData[]): B1SaveBatchInput {
    const { hwDevice } = getState()
    const fwVersion = hwDevice?.fwVersion

    // TODO: verify there are no duplicates
    const spans = sleepLogsData.map(sld => {
      const { timestamp, hours } = sld

      const hoursSpan = hours.map(h => {
        return {
          skinTemps: h.temperatureSEN,
          ambientTemps: h.temperatureMBO,
          heartRates: h.heartRate.map(h => h.heartRateAvg) as CentiBPM[],
          armTaps: h.motionData.map(m => m.stepCount),
          armRotations: h.motionData.map(m => m.orientationCount),
          armMovements: h.motionData.map(m => m.wakeupCount),
          voltage: h.voltage || (0 as DeciVolt),
        }
      })

      return {
        id: timestamp,
        firmwareVersion: fwVersion!,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone as IANATimezone,
        hours: hoursSpan,
      }
    })

    return {
      spans,
    }
  }

  @decorate({
    loaderType: LoaderType.GHOST,
  })
  public async sendBandData(payload: B1SaveBatchInput): Promise<void> {
    this.ongoingSyncingState.set(TemperatureDataState.CALCULATING)
    this.analyticsService.trackEvent(EVENT.BAND_DATA_SAVE_STARTED, {
      spanCount: payload.spans.length,
      indexes: JSON.stringify(payload.spans.map(s => s.id)),
    })

    await api
      .put('b1/saveBatch', {
        json: payload,
      })
      .then(_res => {
        dispatch('clearBandRawData')

        this.analyticsService.trackEvent(EVENT.BAND_DATA_SAVED, {
          spanCount: payload.spans.length,
          indexes: JSON.stringify(payload.spans.map(s => s.id)),
        })

        this.ongoingSyncingState.set(TemperatureDataState.SYNCED)
      })
      .catch(err => {
        let shouldShowError = true
        let shouldClearStash = false

        if (err instanceof HttpRequestError) {
          const statusCode = err.data.responseStatusCode

          // TODO: to be update once design is ready
          shouldShowError = statusCode >= 400 && statusCode < 500
          shouldClearStash = err.data.code === ErrorCode.B1_CORRUPTED_PAYLOAD
        }

        this.analyticsService.trackEvent(EVENT.BAND_DATA_SAVE_FAILED, {
          error: _anyToErrorObject(err),
          spanCount: payload.spans.length,
          indexes: JSON.stringify(payload.spans.map(s => s.id)),
          shouldShowError,
          shouldClearStash,
        })

        if (shouldShowError) void this.errorService.showErrorDialog(err)
        if (shouldClearStash) dispatch('clearBandRawData')

        this.ongoingSyncingState.set(undefined)
      })
  }

  public async checkConnected(): Promise<boolean> {
    const connected = await this.b1PluginService.isConnected()
    try {
      _assert(connected, 'Expects to be connected to B1', {
        code: B1_ERROR.NOT_CONNECTED,
      })
    } catch (err) {
      this.analyticsService.trackEvent(EVENT.BAND_NOT_CONNECTED, {
        error: _anyToErrorObject(err),
        errorString: _stringify(err, {
          includeErrorStack: true,
          includeErrorData: true,
        }),
      })

      this.logService.logBleAction('Expects to be connected to B1 but it is not')
      return false
    }

    return connected
  }

  /**
   * @returns whether app is connected to a band
   */
  public async verifyNotConnected(): Promise<boolean> {
    const connected = await this.b1PluginService.isConnected()
    try {
      _assert(!connected, 'Expects not to be connected to B1', {
        code: B1_ERROR.CONNECTED_WHEN_UNEXPECTED,
      })
    } catch (err) {
      const serialNumber = await this.b1PluginService.getSerialNumber()

      this.analyticsService.trackEvent(EVENT.BAND_CONNECTED_WHEN_UNEXPECTED, {
        name: serialNumber,
        error: _anyToErrorObject(err),
        errorString: _stringify(err, {
          includeErrorStack: true,
          includeErrorData: true,
        }),
      })
      return true
    }

    return connected
  }
}
